// Free-text wishlist editor. Users add 5-10 short "looking for" lines that
// surface on their public profile postcard. Cap and per-item length cap are
// enforced here (DB has the array-length cap; per-item length is client-only
// since Postgres CHECKs can't run subqueries over unnest()).

import { useState } from "react";

const MAX_ITEMS = 10;
const MAX_LEN = 80;

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  /** Compact variant — used inside onboarding cards where vertical space is tight. */
  compact?: boolean;
}

export function LookingForEditor({ value, onChange, compact = false }: Props) {
  const [draft, setDraft] = useState("");

  const trimmed = draft.trim();
  const atCap = value.length >= MAX_ITEMS;
  const duplicate = trimmed.length > 0 && value.some((v) => v.toLowerCase() === trimmed.toLowerCase());
  const canAdd = trimmed.length > 0 && trimmed.length <= MAX_LEN && !atCap && !duplicate;

  const add = () => {
    if (!canAdd) return;
    onChange([...value, trimmed]);
    setDraft("");
  };

  const remove = (i: number) => {
    const next = value.slice();
    next.splice(i, 1);
    onChange(next);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {value.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {value.map((item, i) => (
            <span
              key={`${item}-${i}`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "5px 6px 5px 12px",
                background: "hsl(var(--paper))",
                border: "1px solid hsl(var(--ink))",
                borderRadius: 999,
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 13,
                color: "hsl(var(--ink))",
                maxWidth: "100%",
              }}
            >
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220 }}>
                {item}
              </span>
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label={`Remove ${item}`}
                style={{
                  background: "transparent", border: "none",
                  color: "hsl(var(--ink-2))",
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 14, lineHeight: 1,
                  cursor: "pointer", padding: "0 2px",
                }}
              >×</button>
            </span>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, MAX_LEN))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={atCap ? "10-item max reached" : "e.g. 70s Issey Miyake pleats"}
          disabled={atCap}
          maxLength={MAX_LEN}
          style={{
            flex: 1,
            padding: compact ? "10px 12px" : "12px 14px",
            border: "1px solid hsl(var(--ink))",
            background: "hsl(var(--paper))",
            color: "hsl(var(--ink))",
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 14,
            outline: "none",
            opacity: atCap ? 0.5 : 1,
          }}
        />
        <button
          type="button"
          onClick={add}
          disabled={!canAdd}
          style={{
            padding: compact ? "10px 14px" : "12px 16px",
            background: canAdd ? "hsl(var(--ink))" : "transparent",
            color: canAdd ? "hsl(var(--ink-fg))" : "hsl(var(--ink-2))",
            border: "1px solid hsl(var(--ink))",
            cursor: canAdd ? "pointer" : "not-allowed",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
          }}
        >
          Add
        </button>
      </div>

      <div
        className="caps"
        style={{
          fontSize: 9,
          color: duplicate ? "hsl(var(--rust))" : "hsl(var(--ink-2))",
          letterSpacing: "0.08em",
        }}
      >
        {duplicate
          ? "Already on your list."
          : `${value.length} / ${MAX_ITEMS} items · press enter to add`}
      </div>
    </div>
  );
}
